// src/components/pages/admin-dash/AdminStatCard.jsx
import React from "react";
import Card from "../../ui/Card";
import Icon from "../../ui/Icon";
import Spinner from "../../ui/Spinner";

/**
 * Compact KPI tile for the admin overview.
 *
 * Props:
 *   label      Top mini-label
 *   value      Big number / formatted string
 *   icon       Icon name (Tabler or our inline-Icon map)
 *   loading    Show spinner instead of the value
 *   delta      Optional `{ value: "+12%", positive: true }` chip
 *   tone       "default" | "gold" — gold tints the icon background
 *   onClick    Make the whole tile clickable
 */
export default function AdminStatCard({
  label,
  value,
  icon = "chart-bar",
  loading = false,
  delta = null,
  tone = "default",
  onClick,
  helper,
}) {
  const iconWrapStyle =
    tone === "gold"
      ? {
          background: "var(--gold-dim)",
          color: "var(--gold)",
          border: "1px solid var(--gold-border)",
        }
      : {
          background: "var(--surf-3)",
          color: "var(--txt-2)",
          border: "1px solid var(--border)",
        };

  return (
    <Card
      variant="soft"
      className={onClick ? "transition-all hover:-translate-y-[1px] cursor-pointer" : ""}
      onClick={onClick}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="lg-label mb-1.5">{label}</div>
          <div
            className="font-semibold leading-tight"
            style={{ fontSize: 26, color: "var(--txt)" }}
          >
            {loading ? (
              <span
                className="inline-flex items-center gap-2 text-[14px]"
                style={{ color: "var(--txt-3)" }}
              >
                <Spinner size={14} />
                Loading…
              </span>
            ) : (
              value ?? "—"
            )}
          </div>

          {helper && !loading && (
            <div className="mt-1 text-[11px]" style={{ color: "var(--txt-3)" }}>
              {helper}
            </div>
          )}

          {delta && !loading && (
            <div
              className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-medium"
              style={{ color: delta.positive ? "var(--gold)" : "var(--red)" }}
            >
              <Icon
                name={delta.positive ? "arrow-right" : "arrow-left"}
                size={11}
                style={{
                  transform: delta.positive ? "rotate(-45deg)" : "rotate(-135deg)",
                }}
              />
              {delta.value}
            </div>
          )}
        </div>

        <div
          className="shrink-0 inline-flex items-center justify-center rounded-xl"
          style={{
            width: 38,
            height: 38,
            ...iconWrapStyle,
          }}
        >
          <Icon name={icon} size={18} />
        </div>
      </div>
    </Card>
  );
}
